// Dashboard scene — the grid, the hero feature of the app.
// Ported from the prototype's dashboard.jsx to React hooks + TanStack Query.
//
// Changes from the prototype:
//   - Habit history comes from the `checkins` cache keyed by (habitId, isoDate).
//     We index it into a Set once per render for O(1) lookup inside the grid.
//   - Toggle uses the useToggleCheckin hook (optimistic, offline-tolerant).
//   - Delete uses useUpdateHabit (soft-stop for future months) or useDeleteHabit
//     (hard delete for current/past months), matching original UX.

import { useMemo, useRef, useState, useEffect } from 'react';
import { Icon } from '../components/Icon.jsx';
import { CheckIcon } from '../components/CheckIcon.jsx';
import {
  useHabits, useCheckins, useToggleCheckin, useDeleteHabit, useUpdateHabit,
  useGoals, useCreateGoal, useUpdateGoal, useDeleteGoal,
} from '../lib/hooks.js';
import { MONTHS, MONTH_NAMES_FULL, DOW, ACCENTS, resolveAccentColor, daysInMonth, dowOf, getToday } from '../lib/constants.js';
import { useTheme } from '../lib/themeStore.js';
import { containsSlur } from '../lib/safety.js';
import { sfx } from '../lib/sfx.js';
import { isHabitScheduledOn } from '../lib/habitSchedule.js';
import { EditHabitSheet } from './EditHabitSheet.jsx';
import { GoalSetupModal } from '../components/GoalSetupModal.jsx';
import { parseGoals } from '../lib/goalParse.js';
import {
  shouldPromptMonthly, shouldPromptYearly,
  markMonthlyHandled, markYearlyHandled,
} from '../lib/goalPrompt.js';
import { AIConsentDeclinedError } from '../lib/aiConsent.js';
import { EveningRecapModal } from '../components/EveningRecapModal.jsx';
import { shouldShowToday as shouldShowRecap, markShownToday as markRecapShown } from '../lib/eveningRecap.js';

// AddHabitRow — used inside the grid-left frozen column. Replaces the
// old div+onClick combo. The pointerdown coordinate is tracked in a
// ref; if the pointer travels more than 8 px before the up event, the
// click is treated as a scroll gesture and the New-Habit sheet does NOT
// open. Mobile Safari is the worst offender here — it counts a touch
// that nudges 40+ px during a momentum scroll as a click on the
// originating element, which was making this row pop the modal whenever
// the user dragged across it to scroll the habit list.
function AddHabitRow({ onOpenAdd }) {
  const downPosRef = useRef(null);
  const movedRef = useRef(false);
  const onPointerDown = (e) => {
    downPosRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;
  };
  const onPointerMove = (e) => {
    const start = downPosRef.current;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if ((dx * dx + dy * dy) > 64 /* 8 px */) movedRef.current = true;
  };
  const onPointerUp = () => {
    const moved = movedRef.current;
    downPosRef.current = null;
    movedRef.current = false;
    if (moved) return; // scroll, not a tap
    onOpenAdd();
  };
  const onPointerCancel = () => {
    downPosRef.current = null;
    movedRef.current = false;
  };
  return (
    <button
      type="button"
      className="add-habit-row"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerCancel}
      style={{ width: '100%', textAlign: 'left' }}
    >
      <Icon name="plus" size={14}/>
      Add habit
    </button>
  );
}

function MuteToggle() {
  const [muted, setMuted] = useState(() => sfx.isMuted());
  const onClick = () => {
    const next = sfx.toggle();
    setMuted(next);
    if (!next) sfx.nav();
  };
  return (
    <button className="btn-icon" onClick={onClick} title={muted ? 'Unmute' : 'Mute'}>
      <Icon name={muted ? 'volume-x' : 'volume-2'} size={16}/>
    </button>
  );
}

// Small pill shown in the Dashboard header for guests. Taps open the auth sheet
// so the user can save their local progress before they lose it.
function GuestPill({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 11px', borderRadius: 999,
        background: 'rgba(255,181,71,0.12)',
        border: '1px solid rgba(255,181,71,0.32)',
        color: '#FFC977',
        fontFamily: 'var(--font-display)', fontWeight: 700,
        fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase',
        cursor: 'pointer', whiteSpace: 'nowrap',
        boxShadow: '0 0 10px rgba(255,181,71,0.18)',
      }}
    >
      <Icon name="lock" size={10} color="#FFC977"/>
      Guest · Save progress
    </button>
  );
}

export function Dashboard({ month, year, isGuest, onSetMonth, onOpenAdd, onFirstHabitCreated, onRequestSignIn }) {
  const TODAY = getToday();
  const theme = useTheme();
  const { data: allHabits = [] } = useHabits();
  const { data: checkins = [] } = useCheckins();
  const toggleCheckin = useToggleCheckin();
  const deleteHabit = useDeleteHabit();
  const updateHabit = useUpdateHabit();

  // Goals. These used to live on the Analytics tab; moved here so the
  // planning surface (goals) and the execution surface (habit grid) share
  // one home. Same query/mutation hooks — nothing changed about storage.
  const { data: goals = [] } = useGoals();
  const createGoal = useCreateGoal();
  const updateGoal = useUpdateGoal();
  const deleteGoal = useDeleteGoal();
  const [goalTab, setGoalTab] = useState('monthly');
  const [newGoal, setNewGoal] = useState('');

  // Recap state is declared up here to keep the hook order stable. The
  // memoized derivations + the auto-open useEffect live below, after the
  // `habits`, `doneSet`, `goalSetupKind`, and `editingGoal` `const`s
  // they depend on are initialized — otherwise we hit a temporal dead
  // zone on first render.
  const [recapOpen, setRecapOpen] = useState(false);
  const [recapSaving, setRecapSaving] = useState(false);
  const [recapErr, setRecapErr] = useState('');

  // Long-press / right-click menu for an individual goal row. Opens an
  // Edit / Delete sheet anchored above the tab bar (same pattern as
  // habits). `editingGoal` holds the row that's being inline-edited
  // through a small sheet; null means no edit is open.
  const [menuGoalId, setMenuGoalId] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingGoalText, setEditingGoalText] = useState('');
  const goalPressTimer = useRef(null);

  const startGoalPress = (id) => {
    goalPressTimer.current = setTimeout(() => {
      sfx.nav && sfx.nav();
      setMenuGoalId(id);
    }, 450);
  };
  const cancelGoalPress = () => {
    if (goalPressTimer.current) clearTimeout(goalPressTimer.current);
    goalPressTimer.current = null;
  };

  // Goal-setup modal state. The modal is reused for three entry points:
  //   1. The fresh-month prompt card (kind: 'monthly')
  //   2. The fresh-year prompt card (kind: 'yearly')
  //   3. The "Add by voice" affordance below the input (kind: current tab)
  // We track the kind locally so the modal can label itself correctly
  // and we can persist into the right period.
  const [goalSetupKind, setGoalSetupKind] = useState(null); // null | 'monthly' | 'yearly'
  const [goalSetupSaving, setGoalSetupSaving] = useState(false);
  const [goalSetupErr, setGoalSetupErr] = useState(null);

  const totalDays = daysInMonth(year, month);
  const scrollerRef = useRef(null);
  const [menuHabitId, setMenuHabitId] = useState(null);
  const [confirmHabit, setConfirmHabit] = useState(null);
  const [editingHabit, setEditingHabit] = useState(null);
  const pressTimer = useRef(null);

  // Fire "first habit created" when the count transitions 0 → ≥1 during
  // this session. We gate on the previous value rather than on first render
  // so an existing-user cache load doesn't re-trigger the sheet.
  const prevCountRef = useRef(null);
  useEffect(() => {
    const count = allHabits.length;
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    if (prev === 0 && count >= 1 && typeof onFirstHabitCreated === 'function') {
      const t = setTimeout(() => onFirstHabitCreated(), 600);
      return () => clearTimeout(t);
    }
  }, [allHabits.length, onFirstHabitCreated]);

  // Filter out habits that have been "stopped" before the currently-viewed month.
  const habits = useMemo(() => allHabits.filter(h => {
    if (h.endYear == null || h.endMonth == null) return true;
    const curIdx = year * 12 + month;
    const endIdx = h.endYear * 12 + h.endMonth;
    return curIdx <= endIdx;
  }), [allHabits, year, month]);

  // Build a Set of "habitId|isoDate" once per render — cheap lookup in grid.
  const doneSet = useMemo(() => {
    const s = new Set();
    for (const c of checkins) {
      if (c.status === 'done') s.add(`${c.habitId}|${c.date}`);
    }
    return s;
  }, [checkins]);

  // ── Evening recap derivations ────────────────────────────────────
  // Sits below `habits`, `doneSet`, `goalSetupKind`, `editingGoal` so
  // none of those are in the temporal dead zone when the useMemo /
  // useEffect callbacks run on first render.
  const todayIsoDate = `${TODAY.year}-${String(TODAY.month + 1).padStart(2, '0')}-${String(TODAY.day).padStart(2, '0')}`;
  const habitsToday = useMemo(
    () => habits.filter(h => isHabitScheduledOn(h, TODAY.year, TODAY.month, TODAY.day)),
    [habits, TODAY.year, TODAY.month, TODAY.day]
  );
  const doneTodayCount = useMemo(
    () => habitsToday.filter(h => doneSet.has(`${h.id}|${todayIsoDate}`)).length,
    [habitsToday, doneSet, todayIsoDate]
  );

  // Auto-open the recap modal if it's past the user's configured time
  // AND we haven't shown it today AND there are unfinished habits
  // scheduled today. Skips when another modal is up so we don't trash
  // an open goal-setup or goal-edit sheet.
  useEffect(() => {
    if (recapOpen) return;
    if (goalSetupKind || editingGoal) return;
    if (shouldShowRecap({ habitsToday, doneCountToday: doneTodayCount })) {
      setRecapOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habitsToday.length, doneTodayCount, goalSetupKind, editingGoal]);

  const closeRecap = () => {
    markRecapShown();
    setRecapOpen(false);
    setRecapErr('');
  };

  const handleRecapSave = async (statusMap) => {
    setRecapSaving(true);
    setRecapErr('');
    try {
      const ops = [];
      for (const h of habitsToday) {
        const status = statusMap[h.id];
        const currentlyDone = doneSet.has(`${h.id}|${todayIsoDate}`);
        if (status === 'done' && !currentlyDone) {
          ops.push(toggleCheckin.mutateAsync({ habitId: h.id, isoDate: todayIsoDate, next: true }));
        } else if (status === 'notDone' && currentlyDone) {
          ops.push(toggleCheckin.mutateAsync({ habitId: h.id, isoDate: todayIsoDate, next: false }));
        }
      }
      await Promise.all(ops);
      sfx.ready && sfx.ready();
      closeRecap();
    } catch (err) {
      setRecapErr(err?.message || 'Could not save your recap. Try again?');
    } finally {
      setRecapSaving(false);
    }
  };

  const startPress = (id) => {
    pressTimer.current = setTimeout(() => { sfx.nav(); setMenuHabitId(id); }, 450);
  };
  const cancelPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };

  const isCurrentMonth = (year === TODAY.year && month === TODAY.month);
  const isFutureMonth = (year > TODAY.year) || (year === TODAY.year && month > TODAY.month);
  const today = TODAY.day;
  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  // Build the ISO date string for a (year, month, day) triple (month is 0-indexed).
  const isoFor = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  // Delegates to the shared schedule helper so forward-only frequency
  // overrides from EditHabitSheet are honored here (past days still use
  // the old schedule, new days use the new one).
  const isHabitActiveOn = (h, y, m, d) => isHabitScheduledOn(h, y, m, d);

  // Auto-scroll to today on mount (and when switching months)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const cellWidth = 44;
    const target = isCurrentMonth ? Math.max(0, (today - 6) * cellWidth) : 0;
    const run = () => { if (scrollerRef.current) scrollerRef.current.scrollLeft = target; };
    requestAnimationFrame(run);
    setTimeout(run, 60);
    setTimeout(run, 240);
  }, [month, year, isCurrentMonth, today]);

  const habitRates = habits.map(h => {
    const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}-`;
    const doneCount = days.filter(d => doneSet.has(`${h.id}|${monthPrefix}${String(d).padStart(2, '0')}`)).length;
    const possible = totalDays;
    return {
      done: doneCount,
      possible,
      pct: possible > 0 ? Math.round((doneCount / possible) * 100) : 0,
    };
  });

  const dailyRates = days.map(d => {
    const isFuture = isFutureMonth || (isCurrentMonth && d > today);
    if (isFuture) return { pct: null, future: true };
    const active = habits.filter(h => isHabitActiveOn(h, year, month, d));
    if (active.length === 0) return { pct: null, future: false, na: true };
    const iso = isoFor(year, month, d);
    const done = active.filter(h => doneSet.has(`${h.id}|${iso}`)).length;
    return { pct: Math.round((done / active.length) * 100), done, future: false };
  });

  const totalDone = habitRates.reduce((a, h) => a + h.done, 0);
  const totalPossible = habits.length * totalDays;
  const overallPct = totalPossible > 0 ? Math.round((totalDone / totalPossible) * 100) : 0;
  const currentStreak = habits.length > 0 ? Math.max(...habits.map(h => h.streak || 0), 0) : 0;
  const perfectDays = dailyRates.filter(r => r.pct === 100).length;

  // Goals — the "period" encodes the scope so a month's goals stay pinned
  // to that month even as the user scrubs between months. Yearly goals
  // follow the viewed year.
  const currentPeriod = goalTab === 'monthly'
    ? `${year}-${String(month + 1).padStart(2, '0')}`
    : String(year);

  const currentGoals = useMemo(
    () => goals.filter(g => g.kind === goalTab && g.period === currentPeriod),
    [goals, goalTab, currentPeriod]
  );

  // Periods used by the prompt scheduler — these are about THIS calendar
  // moment, not the (possibly future-dated) month/year the user is viewing.
  // We don't want a "set May goals" prompt if the user is just paging
  // ahead to look at June.
  const todayMonthlyPeriod = `${TODAY.year}-${String(TODAY.month + 1).padStart(2, '0')}`;
  const todayYearlyPeriod = String(TODAY.year);
  const monthlyGoalsForToday = useMemo(
    () => goals.filter(g => g.kind === 'monthly' && g.period === todayMonthlyPeriod),
    [goals, todayMonthlyPeriod]
  );
  const yearlyGoalsForToday = useMemo(
    () => goals.filter(g => g.kind === 'yearly'  && g.period === todayYearlyPeriod),
    [goals, todayYearlyPeriod]
  );

  // Show monthly prompt first (highest cadence). Only fall through to the
  // yearly prompt when the monthly one is satisfied / dismissed. This keeps
  // a single card visible on the dashboard at any given time.
  const promptMonthly = shouldPromptMonthly({
    period: todayMonthlyPeriod, goalCount: monthlyGoalsForToday.length,
  });
  const promptYearly = !promptMonthly && shouldPromptYearly({
    period: todayYearlyPeriod, goalCount: yearlyGoalsForToday.length,
  });
  const promptKind = promptMonthly ? 'monthly' : (promptYearly ? 'yearly' : null);

  const dismissPrompt = () => {
    if (promptKind === 'monthly') markMonthlyHandled(todayMonthlyPeriod);
    else if (promptKind === 'yearly') markYearlyHandled(todayYearlyPeriod);
    sfx.sheetClose && sfx.sheetClose();
    // Force a re-render so the card disappears.
    setGoalTab(t => t);
  };

  // Persist N goals at once. Used by both the fresh-period prompt and the
  // "Add by voice" entry point on the input row. Errors bubble up so the
  // modal can show them and stay open; success closes the modal.
  const persistGoalsFromText = async ({ kind, text }) => {
    setGoalSetupSaving(true);
    setGoalSetupErr(null);
    sfx.startSaving && sfx.startSaving();
    const period = kind === 'monthly' ? todayMonthlyPeriod : todayYearlyPeriod;
    try {
      let parsed;
      try {
        parsed = await parseGoals(text, kind);
      } catch (err) {
        if (err instanceof AIConsentDeclinedError) {
          // User declined the AI consent — keep the raw text as a single
          // goal rather than losing what they typed.
          parsed = [text.trim()];
        } else {
          throw err;
        }
      }
      const cleaned = parsed
        .map(s => s.trim())
        .filter(Boolean)
        .filter(s => !(containsSlur && containsSlur(s)));
      if (!cleaned.length) {
        setGoalSetupErr('I couldn\'t pull any goals out of that. Try again?');
        return false;
      }
      for (const goalText of cleaned) {
        // Sequential awaits keep order stable in the optimistic cache.
        await createGoal.mutateAsync({ kind, period, text: goalText });
      }
      // Mark the prompt handled so the card stops nagging this period.
      if (kind === 'monthly') markMonthlyHandled(todayMonthlyPeriod);
      else if (kind === 'yearly') markYearlyHandled(todayYearlyPeriod);
      sfx.ready && sfx.ready();
      return true;
    } catch (err) {
      setGoalSetupErr(err?.message || 'Could not save your goals. Try again?');
      return false;
    } finally {
      sfx.stopSaving && sfx.stopSaving();
      setGoalSetupSaving(false);
    }
  };

  const handleSaveGoalSetup = async (text) => {
    const ok = await persistGoalsFromText({ kind: goalSetupKind, text });
    if (ok) {
      setGoalSetupKind(null);
      // If the user just satisfied the prompt, switch the goal tab to
      // match what they just set so they immediately see them.
      setGoalTab(goalSetupKind);
    }
  };

  const handleToggleGoal = (g) => {
    updateGoal.mutate({ id: g.id, patch: { checked: !g.checked } });
    sfx.tick && sfx.tick();
  };

  const handleAddGoal = (e) => {
    e.preventDefault();
    const v = newGoal.trim();
    if (!v) return;
    if (containsSlur && containsSlur(v)) {
      sfx.nav && sfx.nav();
      setNewGoal('');
      return;
    }
    createGoal.mutate({ kind: goalTab, period: currentPeriod, text: v });
    setNewGoal('');
    sfx.tick && sfx.tick();
  };

  const onToggleCell = (habit, d) => {
    const iso = isoFor(year, month, d);
    const isDone = doneSet.has(`${habit.id}|${iso}`);
    const next = !isDone;
    next ? sfx.check() : sfx.uncheck();
    toggleCheckin.mutate({ habitId: habit.id, isoDate: iso, next });
    // Milestone SFX on today's streak crossings.
    if (next && year === TODAY.year && month === TODAY.month && d === today) {
      const newStreak = (habit.streak || 0) + 1;
      if ([7, 14, 30, 50, 100].includes(newStreak)) {
        setTimeout(() => sfx.milestone(), 260);
      }
    }
  };

  const handleDelete = (id) => {
    sfx.del();
    const viewedIsFuture = (year > TODAY.year) || (year === TODAY.year && month > TODAY.month);
    if (!viewedIsFuture) {
      deleteHabit.mutate(id);
      return;
    }
    // Stop at end of month PRECEDING the viewed one.
    let stopY = year, stopM = month - 1;
    if (stopM < 0) { stopM = 11; stopY -= 1; }
    updateHabit.mutate({ id, patch: { endYear: stopY, endMonth: stopM } });
  };

  // Empty state
  if (habits.length === 0) {
    return (
      <div className="fade-in" style={{ paddingTop: 4 }}>
        <div style={{ padding: '8px 20px 10px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 32, flexShrink: 0 }}/>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <div className="eyebrow" style={{ fontSize: 9 }}>Daily Ritual Tracker</div>
            <h1 className="title" style={{ marginTop: 6, fontSize: 22 }}>
              The <span className="accent">Consistency</span> Dashboard
            </h1>
            {isGuest && (
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                <GuestPill onClick={onRequestSignIn}/>
              </div>
            )}
          </div>
          <MuteToggle/>
        </div>

        <div style={{ padding: '48px 24px 24px' }}>
          <div style={{
            padding: 28, borderRadius: 22, textAlign: 'center',
            background: 'radial-gradient(circle at 100% 0%, rgba(255,107,77,0.22), transparent 55%), radial-gradient(circle at 0% 100%, rgba(45,156,255,0.22), transparent 55%), rgba(19,21,25,0.72)',
            backdropFilter: 'blur(30px) saturate(180%)',
            border: '1px solid var(--stroke-1)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
          }}>
            <div style={{
              width: 64, height: 64, margin: '0 auto 16px', borderRadius: 18,
              background: 'linear-gradient(135deg, rgba(255,107,77,0.35), rgba(45,156,255,0.35))',
              border: '1px solid rgba(255,255,255,0.12)',
              display: 'grid', placeItems: 'center',
              boxShadow: '0 0 28px rgba(45,156,255,0.3)',
            }}>
              <Icon name="sparkles" size={28} color="#fff"/>
            </div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Welcome</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color: 'var(--fg-1)', letterSpacing: '-0.02em', marginBottom: 10, textWrap: 'balance' }}>
              We are what we <span className="accent">repeatedly</span> do.
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.55, marginBottom: 22, textWrap: 'pretty' }}>
              Start by adding the 3–5 habits you want to stay consistent on. You can adjust them anytime.
            </p>
            <button
              onClick={onOpenAdd}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                background: 'var(--grad-cta)', border: 'none', color: '#ffffff',
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14,
                letterSpacing: 0.02, cursor: 'pointer',
                boxShadow: '0 0 18px rgba(45,156,255,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              + Add your first habit
            </button>
          </div>

          <div style={{ marginTop: 18, padding: '12px 14px', borderRadius: 14, background: 'rgba(19,21,25,0.5)', border: '1px solid var(--stroke-1)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <Icon name="clipboard" size={14} color="var(--accent-hi)"/>
            <div style={{ flex: 1, fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', lineHeight: 1.55 }}>
              Once you've added habits, head to <strong style={{ color: 'var(--fg-1)' }}>Agenda</strong> to set your name and dictate today's plan.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ paddingTop: 4, position: 'relative' }}>
      <div style={{ padding: '8px 20px 10px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 32, flexShrink: 0 }}/>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div className="eyebrow" style={{ fontSize: 9 }}>Daily Ritual Tracker</div>
          <h1 className="title" style={{ marginTop: 6, fontSize: 22 }}>
            The <span className="accent">Consistency</span> Dashboard
          </h1>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-3)', marginTop: 4, fontStyle: 'italic' }}>
            We are what we repeatedly do.
          </div>
          {isGuest && (
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
              <GuestPill onClick={onRequestSignIn}/>
            </div>
          )}
        </div>
        <MuteToggle/>
      </div>

      {/* Month strip */}
      <div className="month-strip">
        {MONTHS.map((m, i) => (
          <button
            key={m}
            className={'month-pill' + (i === month ? ' on' : '')}
            onClick={() => onSetMonth(year, i)}
          >
            {m}
          </button>
        ))}
      </div>

      {/* Summary tiles */}
      {isFutureMonth ? (
        <div className="summary-grid">
          <div className="summary-tile" style={{ gridColumn: 'span 2' }}>
            <div className="val">{habits.length}</div>
            <div className="lbl">Habits</div>
          </div>
          <div className="summary-tile accent" style={{ gridColumn: 'span 2' }}>
            {/* lineHeight: 1.25 override is deliberate. The shared .summary-tile
                .val rule uses line-height:1 so numeric tiles stay tight, but
                "Planning" has a descender on the `g` which gets sliced by the
                tile's overflow:hidden (needed for the rounded corners) — and
                the -webkit-background-clip:text gradient makes the clip even
                more visible. Bumping line-height just on this one tile gives
                the descender the few pixels of vertical room it needs without
                touching the other tiles. */}
            <div className="val" style={{ fontSize: 17, letterSpacing: '0.01em', lineHeight: 1.25 }}>Planning</div>
            <div className="lbl">{MONTH_NAMES_FULL[month]} {year}</div>
          </div>
        </div>
      ) : (
        <div className="summary-grid">
          <div className="summary-tile">
            <div className="val">{totalDone}</div>
            <div className="lbl">Completed</div>
          </div>
          <div className="summary-tile">
            <div className="val">{Math.max(0, totalPossible - totalDone)}</div>
            <div className="lbl">Remaining</div>
          </div>
          <div className="summary-tile accent">
            <div className="val">{overallPct}<span style={{ fontSize: 14 }}>%</span></div>
            <div className="lbl">Progress</div>
          </div>
          <div className="summary-tile">
            <div className="val" style={{ color: currentStreak > 0 ? '#FFB547' : '#fff' }}>{currentStreak}</div>
            <div className="lbl">Day Streak</div>
          </div>
        </div>
      )}

      {/* The big grid */}
      <div className="grid-wrap">
        <div className="grid-header">
          <h3>{MONTH_NAMES_FULL[month]} {year}</h3>
          <div className="sub">
            {isFutureMonth ? (
              <span style={{ color: 'var(--fg-4)' }}>Upcoming · scroll →</span>
            ) : (
              <>
                <span style={{ color: 'var(--accent-hi)' }}>{totalDone}</span>
                <span style={{ color: 'var(--fg-4)' }}> / {totalPossible} checks · scroll →</span>
              </>
            )}
          </div>
        </div>

        <div className="grid-scroller">
          {/* Left frozen column — habit names */}
          <div className="grid-left">
            <div className="row-head">Habit</div>
            {habits.map(h => (
              <div
                key={h.id}
                className="habit-row"
                style={{ position: 'relative', userSelect: 'none', WebkitUserSelect: 'none' }}
                onMouseDown={() => startPress(h.id)}
                onMouseUp={cancelPress}
                onMouseLeave={cancelPress}
                onTouchStart={() => startPress(h.id)}
                onTouchEnd={cancelPress}
                onTouchCancel={cancelPress}
                onContextMenu={(e) => { e.preventDefault(); sfx.nav(); setMenuHabitId(h.id); }}
              >
                <div className="habit-icon" style={{
                  background: `linear-gradient(135deg, ${resolveAccentColor(h.accent, theme)}28, ${resolveAccentColor(h.accent, theme)}0C)`,
                  borderColor: `${resolveAccentColor(h.accent, theme)}44`,
                }}>
                  <Icon name={h.icon} size={14} color={resolveAccentColor(h.accent, theme)}/>
                </div>
                <div className="habit-name">{h.name}</div>
              </div>
            ))}
            {/* Add-habit row — converted from a bare <div onClick> to a
                <button> with a pointer-movement guard. The previous div
                fired its click on touch release even when the gesture
                was a scroll, so users who began a vertical or horizontal
                scroll with their finger anywhere on this strip would
                find the New-Habit sheet popping open at the end of the
                scroll. We now record the pointerdown coordinate and
                only honor the click if the pointer hasn't moved more
                than the standard 8 px tap threshold by the time it lifts. */}
            <AddHabitRow onOpenAdd={onOpenAdd}/>
            <div className="totals-head">Daily %</div>
          </div>

          {/* Scrollable middle */}
          <div className="grid-right" ref={scrollerRef}>
            <div style={{ display: 'flex', minWidth: 'max-content' }}>
              <div>
                <div className="day-cols">
                  {days.map(d => {
                    const isToday = isCurrentMonth && d === today;
                    const dow = dowOf(year, month, d);
                    return (
                      <div key={d} className="day-col">
                        <div className={'day-lbl' + (isToday ? ' today' : '')}>
                          <span>{d}</span>
                          <span className="dow">{DOW[dow]}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {habits.map((h) => {
                  const accent = resolveAccentColor(h.accent, theme);
                  return (
                    <div key={h.id} className="day-cols">
                      {days.map(d => {
                        const iso = isoFor(year, month, d);
                        const done = doneSet.has(`${h.id}|${iso}`);
                        const isFuture = isFutureMonth || (isCurrentMonth && d > today);
                        const isToday = isCurrentMonth && d === today;
                        // Scheduling goes through the shared helper, which
                        // honors any forward-only frequency override set by
                        // EditHabitSheet — past days keep their prior rule.
                        const isInactive = !isHabitScheduledOn(h, year, month, d);
                        const disabled = isFuture || isInactive;
                        return (
                          <div key={d} className={'day-col' + (isFuture ? ' future' : '') + (isInactive ? ' inactive' : '')}>
                            <div
                              className={'cell' + (isFuture ? ' future' : '') + (isInactive ? ' inactive' : '')}
                              onClick={() => !disabled && onToggleCell(h, d)}
                              title={isInactive ? `Not scheduled (${h.frequency || 'Daily'})` : undefined}
                            >
                              <div
                                className={'check' + (done ? ' done' : '') + (disabled ? ' future' : '') + (isToday && !done && !isInactive ? ' today' : '')}
                                style={done ? {
                                  background: `linear-gradient(135deg, ${accent}, ${accent}DD)`,
                                  boxShadow: `0 0 12px ${accent}88, inset 0 1px 0 rgba(255,255,255,0.3)`,
                                } : {}}
                              >
                                {done && <CheckIcon size={16} color="#fff" stroke={2.6}/>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                <div className="day-cols" style={{ height: 44, borderTop: '1px dashed rgba(255,255,255,0.08)' }}/>

                <div className="day-cols totals-row">
                  {dailyRates.map((r, i) => (
                    <div key={i} className="day-col">
                      <div className={'totals-cell' + ((r.future || r.na) ? ' zero' : (r.pct > 0 ? ' strong' : ' zero'))}>
                        {(r.future || r.na) ? '–' : `${r.pct}%`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly column */}
              <div className="monthly-col">
                <div className="monthly-head">Monthly</div>
                {habits.map((h, i) => (
                  <div key={h.id} className="row-pct">
                    {isFutureMonth ? (
                      <span style={{ color: 'var(--fg-4)', fontFamily: 'var(--font-body)', fontSize: 11 }}>–</span>
                    ) : (
                      <>
                        <div className="bar">
                          <div className="bar-fill" style={{ width: `${habitRates[i].pct}%` }}/>
                        </div>
                        <span>{habitRates[i].pct}%</span>
                      </>
                    )}
                  </div>
                ))}
                <div className="row-pct" style={{ height: 44, borderTop: '1px dashed rgba(255,255,255,0.08)' }}/>
                <div className="row-pct totals-row" style={{ height: 42, fontSize: 14, background: 'linear-gradient(90deg, rgba(45,156,255,0.18), rgba(71,166,255,0.12))' }}>
                  <span className="grad-text">{isFutureMonth ? '–' : `${overallPct}%`}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Goal-setup prompt card — surfaces once at the start of a fresh
          month or year (or whenever no goals exist for the current
          period). The user can dismiss it with × and we'll respect that
          for the rest of the period. */}
      {promptKind && (
        <div style={{ padding: '16px 20px 0' }}>
          <div
            className="card"
            style={{
              padding: '14px 16px',
              display: 'flex', gap: 12, alignItems: 'flex-start',
              background: 'linear-gradient(180deg, rgba(71,166,255,0.14), rgba(45,125,224,0.06))',
              border: '1px solid rgba(71,166,255,0.32)',
              boxShadow: '0 8px 22px rgba(45,125,224,0.18)',
            }}
          >
            <div style={{
              width: 30, height: 30, borderRadius: 9,
              background: 'rgba(71,166,255,0.22)',
              border: '1px solid rgba(71,166,255,0.4)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
              color: 'var(--accent-hi)',
            }}>
              <Icon name={promptKind === 'monthly' ? 'target' : 'star'} size={15}/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                color: 'var(--fg-1)',
              }}>
                {promptKind === 'monthly'
                  ? `Set your goals for ${MONTH_NAMES_FULL[TODAY.month]}`
                  : `Set your goals for ${TODAY.year}`}
              </div>
              <div style={{
                fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)',
                marginTop: 2, lineHeight: 1.45,
              }}>
                {promptKind === 'monthly'
                  ? 'Type or speak what you want this month to look like.'
                  : 'A handful of yearly anchors keeps the months honest.'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={() => { sfx.nav && sfx.nav(); setGoalSetupKind(promptKind); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 999,
                    // Theme-aware gradient + glow. var(--grad-cta) is the
                    // moss gradient on Relaxed and the cosmic-blue gradient
                    // on Cosmic, so the same button reads correctly on both
                    // without any CSS override gymnastics. Same approach
                    // used for the primary CTA elsewhere in the app.
                    background: 'var(--grad-cta)',
                    border: '1px solid rgba(var(--accent-hi-rgb), 0.55)',
                    color: '#ffffff',
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12.5,
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(var(--accent-deep-rgb), 0.4)',
                  }}
                >
                  <Icon name="mic" size={12}/>
                  Set my goals
                </button>
                <button
                  onClick={dismissPrompt}
                  style={{
                    padding: '8px 12px', borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--stroke-1)',
                    color: 'var(--fg-3)',
                    fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12.5,
                    cursor: 'pointer',
                  }}
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Goals — moved here from the Analytics tab. Sits under the habit
          grid so the planning surface and the execution surface live
          together. Monthly goals follow the viewed month; yearly goals
          follow the viewed year. */}
      <div className="goal-block" style={{ marginTop: 16 }}>
        <div className="goal-head">
          <div className="icon">
            <Icon name={goalTab === 'monthly' ? 'target' : 'star'} size={15} color="#47A6FF"/>
          </div>
          <h4>{goalTab === 'monthly' ? 'Monthly' : 'Yearly'} Goals</h4>
          <span className="goal-count">
            {currentGoals.filter(g => g.checked).length}<span style={{ color: 'var(--fg-4)' }}>/{currentGoals.length}</span>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, padding: '10px 16px', borderBottom: '1px solid var(--stroke-1)' }}>
          <button className={'chip-opt' + (goalTab === 'monthly' ? ' on' : '')} onClick={() => setGoalTab('monthly')}>Monthly</button>
          <button className={'chip-opt' + (goalTab === 'yearly' ? ' on' : '')} onClick={() => setGoalTab('yearly')}>Yearly</button>
          <div className="spacer" style={{ flex: 1 }}/>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--fg-4)', alignSelf: 'center' }}>
            {goalTab === 'monthly' ? MONTH_NAMES_FULL[month].toUpperCase() : year}
          </span>
        </div>
        {currentGoals.map(g => (
          <div
            key={g.id}
            className="goal-item"
            onClick={() => handleToggleGoal(g)}
            onPointerDown={() => startGoalPress(g.id)}
            onPointerUp={cancelGoalPress}
            onPointerLeave={cancelGoalPress}
            onPointerCancel={cancelGoalPress}
            onContextMenu={(e) => { e.preventDefault(); sfx.nav && sfx.nav(); setMenuGoalId(g.id); }}
            style={{ touchAction: 'manipulation' }}
          >
            <div className={'goal-check' + (g.checked ? ' done' : '')}>
              {g.checked && <CheckIcon size={14} color="#fff" stroke={2.6}/>}
            </div>
            <div className={'goal-text' + (g.checked ? ' done' : '')}>{g.text}</div>
          </div>
        ))}
        <form
          onSubmit={handleAddGoal}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderTop: '1px dashed rgba(255,255,255,0.08)' }}
        >
          <Icon name="plus" size={14} color="#6E7280"/>
          <input
            className="input"
            style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 13, flex: 1 }}
            placeholder="Add a new goal…"
            value={newGoal}
            onChange={e => setNewGoal(e.target.value)}
          />
          {/* Voice entry point — opens the modal in voice mode keyed to
              whichever tab the user is currently looking at. */}
          <button
            type="button"
            onClick={() => { sfx.nav && sfx.nav(); setGoalSetupKind(goalTab); }}
            title={`Add a ${goalTab} goal by voice`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 10px', borderRadius: 999,
              background: 'rgba(71,166,255,0.12)',
              border: '1px solid rgba(71,166,255,0.32)',
              color: 'var(--accent-hi)',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11.5,
              cursor: 'pointer',
            }}
          >
            <Icon name="mic" size={12}/>
            Voice
          </button>
        </form>
      </div>

      {/* Goal setup / voice-add modal. Reuses one component for the
          fresh-period prompt AND the always-on voice entry point. */}
      <GoalSetupModal
        open={!!goalSetupKind}
        title={
          goalSetupKind === 'monthly'
            ? `Goals for ${MONTH_NAMES_FULL[TODAY.month]}`
            : goalSetupKind === 'yearly'
              ? `Goals for ${TODAY.year}`
              : 'Goals'
        }
        subtitle={
          goalSetupKind === 'monthly'
            ? 'What do you want this month to look like? One per line, or all in a paragraph.'
            : goalSetupKind === 'yearly'
              ? 'A few yearly anchors. Speak them or type them.'
              : ''
        }
        saving={goalSetupSaving}
        onSave={handleSaveGoalSetup}
        onClose={() => { if (!goalSetupSaving) { setGoalSetupKind(null); setGoalSetupErr(null); } }}
      />
      {/* Evening recap modal — auto-opens on mount when past the
          configured time. The shown-today flag is committed in
          closeRecap() so a single dismissal sticks for the rest of
          the calendar day. */}
      <EveningRecapModal
        open={recapOpen}
        habits={habitsToday}
        doneSet={doneSet}
        isoDate={todayIsoDate}
        saving={recapSaving}
        errorMsg={recapErr}
        onSave={handleRecapSave}
        onClose={closeRecap}
      />

      {goalSetupErr && goalSetupKind && (
        // Surface the persistence error on the parent in case the modal
        // already closed itself; keeps the user informed if a save fails.
        <div style={{
          position: 'fixed', left: 16, right: 16, bottom: 96, zIndex: 199,
          padding: '10px 14px', borderRadius: 12,
          background: 'rgba(255,72,72,0.12)',
          border: '1px solid rgba(255,72,72,0.4)',
          color: '#FFB4B4',
          fontFamily: 'var(--font-body)', fontSize: 12,
        }}>
          {goalSetupErr}
        </div>
      )}

      {/* Goal action menu (long-press / right-click). Mirrors the habit
          long-press menu pattern: dimmed backdrop, sheet anchored above
          the tab bar, Edit / Delete / Cancel rows. */}
      {menuGoalId && (() => {
        const g = goals.find(x => x.id === menuGoalId);
        if (!g) return null;
        return (
          <>
            <div
              onClick={() => setMenuGoalId(null)}
              style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                zIndex: 90, animation: 'fadeIn 140ms ease-out',
              }}
            />
            <div style={{
              position: 'absolute', left: 20, right: 20, zIndex: 91,
              bottom: 'calc(102px + env(safe-area-inset-bottom))',
              background: 'rgba(18,22,30,0.95)',
              border: '1px solid var(--stroke-1)',
              borderRadius: 18,
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              overflow: 'hidden',
              animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
            }}>
              <div style={{ padding: '16px 18px 12px', display: 'flex', gap: 12, alignItems: 'center', borderBottom: '1px solid var(--stroke-1)' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: 'linear-gradient(135deg, rgba(71,166,255,0.22), rgba(45,125,224,0.06))',
                  border: '1px solid rgba(71,166,255,0.35)',
                  display: 'grid', placeItems: 'center',
                }}>
                  <Icon name={g.kind === 'monthly' ? 'target' : 'star'} size={14} color="var(--accent-hi)"/>
                </div>
                <div style={{
                  flex: 1, minWidth: 0,
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14,
                  color: 'var(--fg-1)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {g.text}
                </div>
              </div>
              <button
                onClick={() => {
                  setMenuGoalId(null);
                  setEditingGoal(g);
                  setEditingGoalText(g.text);
                  sfx.sheetOpen && sfx.sheetOpen();
                }}
                style={{
                  width: '100%', padding: '14px 18px',
                  background: 'transparent', border: 'none',
                  display: 'flex', alignItems: 'center', gap: 12,
                  color: 'var(--fg-1)',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', textAlign: 'left',
                  borderBottom: '1px solid var(--stroke-1)',
                }}
              >
                <Icon name="edit" size={15}/>
                Edit goal
              </button>
              <button
                onClick={() => {
                  setMenuGoalId(null);
                  sfx.del && sfx.del();
                  deleteGoal.mutate(g.id);
                }}
                style={{
                  width: '100%', padding: '14px 18px',
                  background: 'transparent', border: 'none',
                  display: 'flex', alignItems: 'center', gap: 12,
                  color: '#FF8699',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <Icon name="trash" size={15}/>
                Delete goal
              </button>
              <button
                onClick={() => setMenuGoalId(null)}
                style={{
                  width: '100%', padding: '13px 18px',
                  background: 'rgba(255,255,255,0.03)',
                  border: 'none', borderTop: '1px solid var(--stroke-1)',
                  color: 'var(--fg-3)',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        );
      })()}

      {/* Inline goal edit sheet. Light-touch: just a textarea + Save /
          Cancel. Larger restructuring (re-parenting to a different month
          / kind) intentionally not supported here — the user can delete
          and re-add for that. */}
      {editingGoal && (
        <>
          <div
            onClick={() => { setEditingGoal(null); setEditingGoalText(''); }}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              zIndex: 200, animation: 'fadeIn 140ms ease-out',
            }}
          />
          {/* Flex-centered wrapper — separates centering from the
              sheetIn animation so the modal doesn't flash at the
              top-left corner before snapping center. */}
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 201,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16,
              pointerEvents: 'none',
            }}
          >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: '100%',
              maxWidth: 480,
              maxHeight: '85vh',
              overflowY: 'auto',
              background: 'rgba(20,24,32,0.98)',
              border: '1px solid var(--stroke-1)',
              borderRadius: 22,
              padding: '22px 22px 18px',
              boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
              animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
              pointerEvents: 'auto',
            }}
          >
            <div style={{
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17,
              color: 'var(--fg-1)', letterSpacing: '-0.01em',
            }}>
              Edit goal
            </div>
            <textarea
              value={editingGoalText}
              onChange={(e) => setEditingGoalText(e.target.value)}
              rows={3}
              style={{
                width: '100%', resize: 'vertical', minHeight: 80,
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--stroke-1)',
                color: 'var(--fg-1)',
                fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.5,
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                onClick={() => { sfx.sheetClose && sfx.sheetClose(); setEditingGoal(null); setEditingGoalText(''); }}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid var(--stroke-1)',
                  color: 'var(--fg-2)',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const v = editingGoalText.trim();
                  if (!v) return;
                  if (containsSlur && containsSlur(v)) {
                    sfx.nav && sfx.nav();
                    return;
                  }
                  updateGoal.mutate({ id: editingGoal.id, patch: { text: v } });
                  sfx.toast && sfx.toast();
                  setEditingGoal(null);
                  setEditingGoalText('');
                }}
                disabled={!editingGoalText.trim() || editingGoalText.trim() === editingGoal.text.trim()}
                style={{
                  flex: 1.2, padding: '12px 16px', borderRadius: 999,
                  background: editingGoalText.trim() && editingGoalText.trim() !== editingGoal.text.trim()
                    ? 'linear-gradient(180deg, #47A6FF, #2D7DE0)'
                    : 'rgba(255,255,255,0.06)',
                  border: editingGoalText.trim() && editingGoalText.trim() !== editingGoal.text.trim()
                    ? '1px solid rgba(71,166,255,0.55)'
                    : '1px solid var(--stroke-1)',
                  color: editingGoalText.trim() && editingGoalText.trim() !== editingGoal.text.trim() ? '#ffffff' : 'var(--fg-3)',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                  cursor: editingGoalText.trim() && editingGoalText.trim() !== editingGoal.text.trim() ? 'pointer' : 'default',
                  boxShadow: editingGoalText.trim() && editingGoalText.trim() !== editingGoal.text.trim() ? '0 8px 22px rgba(45,125,224,0.4)' : 'none',
                }}
              >
                Save
              </button>
            </div>
          </div>
          </div>
        </>
      )}

      {/* Insight
          ------------------------------------------------------------
          All color tokens for this card go through --accent-rgb so
          the background wash, border, icon chip, and icon itself all
          flow with the active theme. Girlboss → pink; Pro themes →
          their editorial blue; Cosmic → electric blue. */}
      <div style={{ padding: '16px 20px 0' }}>
        <div className="card" style={{
          padding: '14px 16px',
          display: 'flex', gap: 12, alignItems: 'flex-start',
          background: 'linear-gradient(180deg, rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0.02))',
          borderColor: 'rgba(var(--accent-rgb), 0.22)',
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 9,
            background: 'rgba(var(--accent-rgb), 0.18)',
            border: '1px solid rgba(var(--accent-rgb), 0.35)',
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <Icon name="sparkles" size={15} color="var(--accent-hi)"/>
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 12.5, color: 'var(--fg-2)', lineHeight: 1.5 }}>
            {/*
              Both branches begin with a <strong> followed by sibling text
              nodes, so without a key React reconciles them in place and you
              get "13perfectmay. shape your month now" — a mashup of the two
              copies while the DOM updates child-by-child. A keyed wrapper
              forces a full unmount/remount on the isFutureMonth flip.
            */}
            {isFutureMonth ? (
              <span key="insight-future">
                <strong style={{ color: 'var(--fg-1)', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                  Planning {MONTH_NAMES_FULL[month]}.
                </strong>
                {' '}Shape your month now. Add, edit, or remove habits so it's ready when you get here.
              </span>
            ) : (
              <span key="insight-current">
                <strong style={{ color: 'var(--fg-1)', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                  {perfectDays > 0 ? `${perfectDays} perfect ${perfectDays === 1 ? 'day' : 'days'}`: `${overallPct}% this month`}
                </strong>
                {perfectDays > 0 ? ' this month.' : '.'}
                {' '}
                {currentStreak > 0
                  ? `Your longest active streak is ${currentStreak} days on ${habits.find(h => h.streak === currentStreak)?.name}. Keep going.`
                  : 'Small consistent reps compound. Stack one easy win today.'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Habit action menu (long-press) */}
      {menuHabitId && (
        <>
          <div
            onClick={() => setMenuHabitId(null)}
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              zIndex: 90, animation: 'fadeIn 140ms ease-out',
            }}
          />
          <div style={{
            position: 'absolute', left: 20, right: 20, zIndex: 91,
            // Tab bar = 18px bottom inset + 68px height + safe-area inset;
            // we anchor the menu 16px above its top edge so Delete and
            // Cancel never get eaten by the bar on notched devices.
            bottom: 'calc(102px + env(safe-area-inset-bottom))',
            background: 'rgba(18,22,30,0.95)',
            border: '1px solid var(--stroke-1)',
            borderRadius: 18,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            overflow: 'hidden',
            animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
          }}>
            {(() => {
              const h = habits.find(x => x.id === menuHabitId);
              if (!h) return null;
              const accent = resolveAccentColor(h.accent, theme);
              return (
                <>
                  <div style={{ padding: '16px 18px 12px', display: 'flex', gap: 12, alignItems: 'center', borderBottom: '1px solid var(--stroke-1)' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 10,
                      background: `linear-gradient(135deg, ${accent}28, ${accent}0C)`,
                      border: `1px solid ${accent}44`,
                      display: 'grid', placeItems: 'center',
                    }}>
                      <Icon name={h.icon} size={14} color={accent}/>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--fg-1)' }}>
                      {h.name}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // Close the action menu first so the Edit sheet slides
                      // over a clean backdrop instead of stacking on top.
                      const target = habits.find(x => x.id === menuHabitId);
                      setMenuHabitId(null);
                      if (target) setEditingHabit(target);
                    }}
                    style={{
                      width: '100%', padding: '14px 18px',
                      background: 'transparent', border: 'none',
                      display: 'flex', alignItems: 'center', gap: 12,
                      color: 'var(--fg-1)',
                      fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14,
                      cursor: 'pointer', textAlign: 'left',
                      borderBottom: '1px solid var(--stroke-1)',
                    }}
                  >
                    <Icon name="edit" size={15}/>
                    Edit habit
                  </button>
                  <button
                    onClick={() => {
                      setMenuHabitId(null);
                      setConfirmHabit({ id: h.id, name: h.name, mode: isFutureMonth ? 'stop' : 'delete' });
                    }}
                    style={{
                      width: '100%', padding: '14px 18px',
                      background: 'transparent', border: 'none',
                      display: 'flex', alignItems: 'center', gap: 12,
                      color: '#FF8699',
                      fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14,
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <Icon name="trash" size={15}/>
                    {isFutureMonth ? `Stop from ${MONTH_NAMES_FULL[month]}` : 'Delete habit'}
                  </button>
                  <button
                    onClick={() => setMenuHabitId(null)}
                    style={{
                      width: '100%', padding: '13px 18px',
                      background: 'rgba(255,255,255,0.03)',
                      border: 'none', borderTop: '1px solid var(--stroke-1)',
                      color: 'var(--fg-3)',
                      fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* Delete confirmation */}
      {confirmHabit && (
        <>
          <div
            onClick={() => setConfirmHabit(null)}
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              zIndex: 92, animation: 'fadeIn 140ms ease-out',
            }}
          />
          <div style={{
            position: 'absolute', left: 24, right: 24, top: '40%', zIndex: 93,
            background: 'rgba(20,24,32,0.98)',
            border: '1px solid var(--stroke-1)',
            borderRadius: 20,
            padding: '22px 20px 16px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
            animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--fg-1)', marginBottom: 8 }}>
              {confirmHabit.mode === 'stop'
                ? `Stop "${confirmHabit.name}" from ${MONTH_NAMES_FULL[month]}?`
                : `Delete "${confirmHabit.name}"?`}
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)', lineHeight: 1.5, marginBottom: 18 }}>
              {confirmHabit.mode === 'stop'
                ? `The habit will stay in your previous months with all its history. It won't appear in ${MONTH_NAMES_FULL[month]} ${year} onward.`
                : "All check-ins and streaks for this habit will be removed. This can't be undone."}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmHabit(null)}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid var(--stroke-1)',
                  color: 'var(--fg-2)',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const id = confirmHabit.id;
                  setConfirmHabit(null);
                  handleDelete(id);
                }}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 999,
                  background: 'linear-gradient(180deg, #FF5A70, #E23A54)',
                  border: '1px solid rgba(255,90,112,0.6)',
                  color: '#ffffff',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                  cursor: 'pointer',
                  boxShadow: '0 8px 22px rgba(226,58,84,0.45)',
                }}
              >
                {confirmHabit.mode === 'stop' ? 'Stop' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit-habit sheet. Mounted unconditionally so the close animation
          plays back out; we pass `open` based on whether `editingHabit` is
          set and keep the habit prop one render past close so the body
          doesn't snap to empty during the slide-down. */}
      <EditHabitSheet
        open={!!editingHabit}
        habit={editingHabit}
        onClose={() => setEditingHabit(null)}
      />
    </div>
  );
}
